/**
 * CSVImport — dual-mode bulk importer for Courses and Universities.
 *
 * Modes:
 *   - "courses"      → uses multipart POST /api/courses/import-csv
 *                      (server parses + returns counts)
 *   - "universities" → parses CSV client-side, POSTs JSON to
 *                      /api/admin/universities/bulk-upload
 *
 * Props:
 *   defaultMode: 'courses' | 'universities'  (default 'courses')
 *   onImportComplete: callback fired after a successful upload
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Upload, Download, FileSpreadsheet, Loader2, CheckCircle, AlertCircle, Building2, GraduationCap,
} from 'lucide-react';
import api from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Universities template (frontend-generated since backend has no endpoint)
const UNIVERSITIES_TEMPLATE = (
  'name,country,city,website,description\n' +
  'University of Manchester,United Kingdom,Manchester,https://www.manchester.ac.uk,Russell Group research university\n' +
  'University of Sydney,Australia,Sydney,https://www.sydney.edu.au,Group of Eight research university\n'
);

// Lightweight CSV parser supporting quoted fields with commas + double-quote escapes.
function parseCSV(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else { inQuotes = false; }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((v) => v !== '')) rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length) {
    row.push(field);
    if (row.some((v) => v !== '')) rows.push(row);
  }
  if (rows.length === 0) return { headers: [], records: [] };
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const records = rows.slice(1).map((r) => {
    const o = {};
    headers.forEach((h, i) => { o[h] = (r[i] || '').trim(); });
    return o;
  });
  return { headers, records };
}

const MODE_CONFIG = {
  courses: {
    label: 'Courses',
    Icon: GraduationCap,
    title: 'Import Courses from CSV',
    subtitle: 'Bulk add courses using a spreadsheet',
    accent: 'bg-teal-600',
    accentLight: 'bg-teal-600/10',
    accentText: 'text-teal-600',
    requiredCols: ['university_name', 'course_name', 'degree_level', 'field_of_study'],
    optionalCols: ['tuition_fee', 'duration_months', 'intake_dates', 'commission_percentage', 'ielts_requirement', 'course_url', 'country'],
  },
  universities: {
    label: 'Universities',
    Icon: Building2,
    title: 'Import Universities from CSV',
    subtitle: 'Bulk add universities. Idempotent on (name, country).',
    accent: 'bg-indigo-600',
    accentLight: 'bg-indigo-600/10',
    accentText: 'text-indigo-600',
    requiredCols: ['name', 'country'],
    optionalCols: ['city', 'website', 'description'],
  },
};

const CSVImport = ({ defaultMode = 'courses', onImportComplete }) => {
  const [mode, setMode] = useState(MODE_CONFIG[defaultMode] ? defaultMode : 'courses');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const cfg = MODE_CONFIG[mode];

  const switchMode = (m) => {
    if (uploading) return;
    setMode(m);
    setResult(null);
  };

  // ---------------- Template download ----------------
  const handleDownloadTemplate = async () => {
    try {
      let csvText = '';
      if (mode === 'courses') {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/courses/export-template`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        csvText = data.template;
      } else {
        csvText = UNIVERSITIES_TEMPLATE;
      }
      const blob = new Blob([csvText], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${mode}_import_template.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    }
  };

  // ---------------- Upload ----------------
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const lname = file.name.toLowerCase();
    // Courses mode accepts both CSV and XLSX; other modes remain CSV-only.
    const isCsv = lname.endsWith('.csv');
    const isXlsx = lname.endsWith('.xlsx') || lname.endsWith('.xls');
    if (mode === 'courses') {
      if (!isCsv && !isXlsx) {
        toast.error('Please upload a CSV or XLSX file');
        return;
      }
    } else if (!isCsv) {
      toast.error('Please upload a CSV file');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      if (mode === 'courses') {
        // Multipart upload to existing backend endpoint
        const formData = new FormData();
        formData.append('file', file);
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/courses/import-csv`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Import failed');
        setResult({ added: data.courses_added, errors: data.errors });
        toast.success(`${data.courses_added} courses imported successfully`);
      } else {
        // Universities: parse client-side, POST JSON
        const text = await file.text();
        const { headers, records } = parseCSV(text);
        if (records.length === 0) throw new Error('CSV is empty or only has a header row');
        for (const col of cfg.requiredCols) {
          if (!headers.includes(col)) throw new Error(`Missing required column: ${col}`);
        }
        const universities = records.map((r) => ({
          name: r.name,
          country: r.country,
          city: r.city || null,
          website: r.website || null,
          description: r.description || null,
        })).filter((u) => u.name && u.country);
        if (universities.length === 0) throw new Error('No valid rows — every row must have name + country');
        const { data } = await api.post('/admin/universities/bulk-upload', { universities });
        setResult({ added: data.created, skipped: data.skipped, errors: data.errors });
        toast.success(`${data.created} universities created${data.skipped ? `, ${data.skipped} already existed` : ''}`);
      }
      if (onImportComplete) onImportComplete();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Import failed';
      toast.error(msg);
      setResult({ error: msg });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const Icon = cfg.Icon;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="csv-import-card">
      {/* Mode tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-5" data-testid="csv-import-tabs">
        {Object.entries(MODE_CONFIG).map(([key, c]) => {
          const TabIcon = c.Icon;
          const active = mode === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => switchMode(key)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid={`csv-import-tab-${key}`}
            >
              <TabIcon className="w-4 h-4" />
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-12 h-12 rounded-lg ${cfg.accentLight} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${cfg.accentText}`} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{cfg.title}</h3>
          <p className="text-sm text-gray-500">{cfg.subtitle}</p>
        </div>
      </div>

      {/* Requirements */}
      <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-100">
        <h4 className="text-gray-800 font-medium mb-2 text-sm">CSV format requirements</h4>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>First row must contain column headers (case-insensitive)</li>
          <li><strong>Required:</strong> {cfg.requiredCols.join(', ')}</li>
          <li><strong>Optional:</strong> {cfg.optionalCols.join(', ')}</li>
          {mode === 'courses' && <li>Multiple intake dates should be comma-separated and quoted (e.g., "Jan,Sep")</li>}
          {mode === 'universities' && <li>Idempotent on name + country (case-insensitive) — existing rows are skipped</li>}
        </ul>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <Button
          onClick={handleDownloadTemplate}
          variant="outline"
          className="border-gray-300 text-gray-700 hover:bg-gray-100"
          data-testid="csv-download-template-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Download Template
        </Button>

        <label className="cursor-pointer flex-1">
          <input
            type="file"
            className="hidden"
            accept={mode === 'courses' ? '.csv,.xlsx,.xls' : '.csv'}
            onChange={handleFileUpload}
            disabled={uploading}
            data-testid="csv-file-input"
          />
          <Button
            asChild
            className={`w-full ${cfg.accent} text-white hover:opacity-90 font-semibold`}
            disabled={uploading}
            data-testid="csv-upload-btn"
          >
            <span>
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing…</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />{mode === 'courses' ? 'Upload CSV / XLSX' : 'Upload CSV File'}</>
              )}
            </span>
          </Button>
        </label>
      </div>

      {/* Result */}
      {result && (
        <div className={`p-4 rounded-lg ${result.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="csv-import-result">
          {result.error ? (
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-red-700 font-medium">Import failed</p>
                <p className="text-sm text-gray-700">{result.error}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-green-700 font-medium">Import successful</p>
                <p className="text-sm text-gray-700">
                  {result.added} {mode} added successfully
                  {typeof result.skipped === 'number' && result.skipped > 0 ? ` · ${result.skipped} already existed` : ''}
                </p>
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-amber-700 font-medium">Warnings ({result.errors.length}):</p>
                    <ul className="text-xs text-gray-600 mt-1 list-disc list-inside">
                      {result.errors.slice(0, 5).map((err, idx) => <li key={idx}>{err}</li>)}
                      {result.errors.length > 5 && <li>… and {result.errors.length - 5} more</li>}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CSVImport;
